import { ref } from 'vue';

import { useProjectsStore } from '@/stores/ProjectsStore.ts';
import { Status, type Control } from '@/clients/v1/index.ts';
import { captureError } from '@/composables/Sentry.ts';

export const controlsMap = ref(new Map<string, Control>());

// Track pending requests to avoid duplicates
const pendingRequests = new Map<string, Promise<void>>();

export const updateControlsMap = () => {
  const projectsStore = useProjectsStore();
  const controlsData = projectsStore.controlsDataLight;

  if (!controlsData) return;

  for (const control of controlsData.items) {
    controlsMap.value.set(control.id, control);
  }

  // Force reactivity (Vue won't detect `.set()` inside Map unless reassigned)
  controlsMap.value = new Map(controlsMap.value);
};

export const getControlsBatch = async (controlIds: string[]) => {
  const projectsStore = useProjectsStore();
  if (!projectsStore.project) return;

  // Deduplicate input controlIds
  const uniqueControlIds = [...new Set(controlIds)];

  const idsToFetch = uniqueControlIds.filter(
    (id) => !controlsMap.value.has(id) && !pendingRequests.has(id),
  );
  if (idsToFetch.length === 0) {
    // Wait for any pending requests for the requested IDs
    const pendingPromises = uniqueControlIds
      .map((id) => pendingRequests.get(id))
      .filter((promise) => promise !== undefined);

    if (pendingPromises.length > 0) {
      await Promise.all(pendingPromises);
    }
    return;
  }

  const params = {
    projectId: projectsStore.project.id,
    controlIds: idsToFetch,
    status: Status.NotExecuted,
  };

  // Create a single promise for this batch and track it for each ID
  const fetchPromise = (async () => {
    try {
      const response = await projectsStore.getProjectControlsLight(params, false, false);

      if (!response) return;

      for (const control of response.items) {
        controlsMap.value.set(control.id, control);
      }

      // Force reactivity (Vue won't detect `.set()` inside Map unless reassigned)
      controlsMap.value = new Map(controlsMap.value);
    } catch (error) {
      captureError(error, {
        message: 'Utils: controlsMap, Function: getControlsBatch',
      });
    } finally {
      // Clean up pending requests
      for (const id of idsToFetch) {
        pendingRequests.delete(id);
      }
    }
  })();

  // Track this promise for each ID being fetched
  for (const id of idsToFetch) {
    pendingRequests.set(id, fetchPromise);
  }

  await fetchPromise;
};
